import { Box, Divider, IconButton, Typography, useMediaQuery } from "@mui/material"
import { useNavigate } from "react-router-dom"

const productLinks = [
    { title: "Why Vibio", href: "#why-vibio" },
    { title: "How it Works", href: "#how-it-works" },
    { title: "Pricing", href: "#pricing" },
    { title: "FAQ", href: "#faq" },
]

const companyLinks = [
    { title: "Terms", nav: "/terms" },
    { title: "Privacy", nav: "/privacy" },
    { title: "Contact Us", href: "mailto:info@vibio.co" },
]

function TikTokIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.79a8.18 8.18 0 004.77 1.53V6.87a4.84 4.84 0 01-1-.18z" />
        </svg>
    )
}

function InstagramIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
    )
}

export default function Footer() {
    const navigate = useNavigate()
    const isMobile = useMediaQuery('(max-width:950px)')

    const styles = {
        container: {
            backgroundColor: 'primary.main',
            color: 'text.regular',
            fontFamily: 'Catamaran',
            p: isMobile ? '2rem 1.5rem' : '3rem 6vw',
        },
        columnsContainer: {
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            gap: isMobile ? 4 : 2,
            mb: 3,
        },
        brandColumn: {
            flex: 2,
            textAlign: isMobile ? 'center' : 'left',
        },
        linkColumn: {
            flex: 1,
            textAlign: isMobile ? 'center' : 'left',
        },
        brandName: {
            fontFamily: 'inherit',
            fontWeight: 900,
            fontSize: '2rem',
            color: 'inherit',
            mb: 0.5,
        },
        tagline: {
            fontFamily: 'inherit',
            fontWeight: 500,
            fontSize: '0.95rem',
            color: 'rgba(255,255,255,0.85)',
            mb: 2,
        },
        columnTitle: {
            fontFamily: 'inherit',
            fontWeight: 800,
            fontSize: '1rem',
            color: 'inherit',
            mb: 1.5,
        },
        link: {
            fontFamily: 'inherit',
            fontWeight: 500,
            fontSize: '0.9rem',
            color: 'rgba(255,255,255,0.85)',
            cursor: 'pointer',
            mb: 0.8,
            '&:hover': { color: '#fff' },
        },
        socialIcon: {
            color: 'inherit',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
        },
        copyright: {
            fontFamily: 'inherit',
            fontWeight: 500,
            fontSize: '0.9rem',
            color: 'rgba(255,255,255,0.95)',
            textAlign: 'center',
            mt: 2,
        },
    }

    return (
        <Box sx={styles.container}>
            <Box sx={styles.columnsContainer}>
                {/* Brand column */}
                <Box sx={styles.brandColumn}>
                    <Typography sx={styles.brandName}>Vibio</Typography>
                    <Typography sx={styles.tagline}>video link in bio</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: isMobile ? 'center' : 'flex-start' }}>
                        <IconButton
                            size="small"
                            sx={styles.socialIcon}
                            aria-label="TikTok"
                            component="a"
                            href="https://www.tiktok.com/@vibio.co"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <TikTokIcon />
                        </IconButton>
                        <IconButton
                            size="small"
                            sx={styles.socialIcon}
                            aria-label="Instagram"
                            component="a"
                            href="https://www.instagram.com/vibio_co/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <InstagramIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Product column */}
                <Box sx={styles.linkColumn}>
                    <Typography sx={styles.columnTitle}>Product</Typography>
                    {productLinks.map((link) => (
                        <Typography
                            key={link.title}
                            component="a"
                            href={link.href}
                            sx={{ ...styles.link, display: 'block', textDecoration: 'none' }}
                        >
                            {link.title}
                        </Typography>
                    ))}
                </Box>

                {/* Company column */}
                <Box sx={styles.linkColumn}>
                    <Typography sx={styles.columnTitle}>Company</Typography>
                    {companyLinks.map((link) => (
                        <Typography
                            key={link.title}
                            component={link.href ? "a" : "span"}
                            href={link.href || undefined}
                            onClick={link.nav ? () => navigate(link.nav) : undefined}
                            sx={{ ...styles.link, display: 'block', textDecoration: 'none' }}
                        >
                            {link.title}
                        </Typography>
                    ))}
                </Box>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

            <Typography sx={styles.copyright}>
                &copy; 2026 Vibio. All rights reserved.
            </Typography>
        </Box>
    )
}
